import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FieldSelector } from '@/components/TimesheetReports/FieldSelector';
import type { ReportColumnsState } from '@/hooks/useReportColumns';
import { Download, Filter, X, Search } from 'lucide-react';
import { AUDIT_DEFAULT_COLUMNS } from '@/utils/AuditTrail/constants';
import type { AuditTrailFiltersState } from '@/hooks/AuditTrail/useAuditTrailFilters';

interface AuditTrailToolbarProps {
  filters: AuditTrailFiltersState;
  columns: ReportColumnsState;
  allowExport: boolean;
  isExporting: boolean;
  onExport: () => void;
}

export const AuditTrailToolbar: React.FC<AuditTrailToolbarProps> = ({ filters, columns, allowExport, isExporting, onExport }) => {
  const {
    appliedFilters,
    searchQuery,
    setSearchQuery,
    quickDateRange,
    handleQuickDateChange,
    handleQuickStatusChange,
    openFilterSheet,
    sheetFilterCount,
  } = filters;
  const { fieldConfigs, setFieldConfigs, fieldPresets, setFieldPresets } = columns;

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4 -mx-6 px-6">
      {/* Search */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search audit trail..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
            onClick={() => setSearchQuery('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="lg:flex-shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range quick filter */}
          <Select value={quickDateRange} onValueChange={handleQuickDateChange}>
            <SelectTrigger className="w-auto h-9 bg-white dark:bg-background px-3">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
            </SelectContent>
          </Select>

          {/* Status quick filter */}
          <Select
            value={
              (appliedFilters.statuses?.length ?? 0) > 1
                ? 'multiple'
                : appliedFilters.statuses?.length === 1
                ? appliedFilters.statuses[0]
                : 'all'
            }
            onValueChange={handleQuickStatusChange}
          >
            <SelectTrigger className="w-auto h-9 bg-white dark:bg-background px-3">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="recalled">Recalled</SelectItem>
              {(appliedFilters.statuses?.length ?? 0) > 1 && (
                <SelectItem value="multiple">
                  Multiple ({appliedFilters.statuses!.length})
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {/* Spacer */}
          <div className="flex-1 min-w-[4px]" />

          {/* Filter icon → side panel */}
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 relative"
            onClick={openFilterSheet}
            title="More filters"
          >
            <Filter className="h-4 w-4" />
            {sheetFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium leading-none">
                {sheetFilterCount}
              </span>
            )}
          </Button>

          {/* Download */}
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={onExport}
            disabled={!allowExport || isExporting}
            title={allowExport ? 'Export to CSV' : "You don't have permission to export"}
          >
            <Download className="h-4 w-4" />
          </Button>

          {/* Configure Columns */}
          <FieldSelector
            fields={fieldConfigs}
            defaultFields={AUDIT_DEFAULT_COLUMNS}
            onFieldsChange={setFieldConfigs}
            storageKey="audit-trail-columns"
            presets={fieldPresets}
            onSavePreset={(preset) => setFieldPresets((prev) => [...prev, preset])}
            onDeletePreset={(id) => setFieldPresets((prev) => prev.filter((p) => p.id !== id))}
          />
        </div>
      </div>
    </div>
  );
};
